import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    setIsDropdownOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const showLightText = isHome && !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : isHome
            ? 'bg-gradient-to-b from-black/40 to-transparent'
            : 'bg-background-50/95 backdrop-blur-md'
      }`}
    >
      <div className="w-full px-4 md:px-10 py-3 md:py-4 flex items-center justify-between">
        <div className="flex items-center gap-4 md:gap-6">
          <Link
            to="/"
            className={`text-lg md:text-xl font-heading font-bold whitespace-nowrap transition-colors ${
              showLightText ? 'text-background-50' : 'text-foreground-950'
            }`}
          >
            Mulebuy Spreadsheets
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                showLightText
                  ? 'text-background-50/80 hover:text-background-50 hover:bg-white/10'
                  : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200/60'
              }`}
            >
              Home
            </Link>
            <Link
              to="/shopping-guide"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                showLightText
                  ? 'text-background-50/80 hover:text-background-50 hover:bg-white/10'
                  : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200/60'
              }`}
            >
              Shopping Guide
            </Link>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                showLightText
                  ? 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200/60'
              }`}
            >
              Categories
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {isDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 bg-background-50 rounded-xl shadow-lg border border-background-200 py-3 min-w-[240px] max-h-[420px] overflow-y-auto z-50">
                <p className="px-4 pb-2 text-xs font-label tracking-wider text-foreground-400 uppercase">Browse by Category</p>
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                  >
                    <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-accent-700 text-sm`}></i>
                    </span>
                    <span>{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Mulebuy Spreadsheet
          </a>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>
        </div>

        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center ${
            showLightText ? 'text-background-50' : 'text-foreground-950'
          }`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4 flex flex-col gap-4 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 py-2.5 text-sm font-medium text-foreground-800 hover:text-foreground-950 whitespace-nowrap"
            >
              <i className="ri-home-line text-base w-5 h-5 flex items-center justify-center"></i>
              Home
            </Link>
            <Link
              to="/shopping-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 py-2.5 text-sm font-medium text-foreground-800 hover:text-foreground-950 whitespace-nowrap"
            >
              <i className="ri-guide-line text-base w-5 h-5 flex items-center justify-center"></i>
              Shopping Guide
            </Link>
          </div>

          <div className="border-t border-background-200 pt-3">
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full py-2.5 text-sm font-semibold text-foreground-800"
            >
              <span className="flex items-center gap-2">
                <i className="ri-apps-line text-base w-5 h-5 flex items-center justify-center"></i>
                Categories ({categories.length})
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileCatOpen && (
              <div className="grid grid-cols-2 gap-1 mt-2 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 py-2 px-2 rounded-md text-xs text-foreground-600 hover:bg-background-100 hover:text-foreground-950 whitespace-nowrap"
                  >
                    <span className="w-6 h-6 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-accent-600 text-xs`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 pt-3 border-t border-background-200">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold text-center whitespace-nowrap"
            >
              Mulebuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold text-center whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <div className="border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-400 mb-2">Quick Links</p>
            <Link
              to="/what-is-mulebuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-1.5 text-xs text-foreground-500 hover:text-foreground-800"
            >
              What is Mulebuy Spreadsheet
            </Link>
            <Link
              to="/blog/best-mulebuy-spreadsheet-list"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-1.5 text-xs text-foreground-500 hover:text-foreground-800"
            >
              Best Mulebuy Spreadsheet List
            </Link>
            <Link
              to="/blog/how-to-use-mulebuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-1.5 text-xs text-foreground-500 hover:text-foreground-800"
            >
              How to Use Mulebuy Spreadsheet
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}